// src/shared/components/Modals/DeliverySummaryModal.jsx
import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Divider,
  Stack,
} from '@mui/material';
import { Package } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { PickupSection } from '../../../pages/SendPackagePage/components/sections/PickupSection'
import { DeliverySection } from '../../../pages/SendPackagePage/components/sections/DeliverySection';
import { BillingSection } from '../../../pages/SendPackagePage/components/sections/BillingSection';
import { SpecialOptionsSection } from '../../../pages/SendPackagePage/components/sections/SpecialOptionsSection';
import { BusTransportSection } from '../../../pages/SendPackagePage/components/BusTransportSection';

export function DeliverySummaryModal({
  open,
  onClose,
  onConfirm,
  delivery,
  onUpdateField,
  onEditBusOption,
  // Pricing
  estimatedPrice,
  timeWindowFee,
  totalPrice,
  selectedTransportLabel,
  // ✅ "edit" = opened from Wrench chip, "confirm" = opened from submit button
  mode = "confirm",
}) {
  const { t } = useTranslation();

  if (!delivery) return null;

  const {
    origin,
    pickupTime,
    latestPickUpTime,
    hasTimeWindow,
    pickupApartment,
    pickupFloor,
    pickupBuilding,
    pickupAccessCode,
    pickupInstructions,
    senderName,
    senderPhone,
    senderEmail,
    destination,
    targetTime,
    earliestTargetTime,
    deliveryApartment,
    deliveryFloor,
    deliveryBuilding,
    deliveryAccessCode,
    deliveryInstructions,
    recipientName,
    recipientPhone,
    recipientEmail,
    paymentMethod,
    yNumber,
    companyName,
    paymentReference,
    billingDepartment,
    returnParcels,
    temperatureControl,
    canBeLeftAtDoor,
    busTransportData,
    returnMethod,
  } = delivery;

  const setter = (field) => (value) => onUpdateField(field, value);

  const isEditMode = mode === "edit";
    // Add this near the top of the component, after destructuring
  const isBusLegInPast = busTransportData?.legs?.[0]?.end && busTransportData.legs[0].end < Date.now();

  const isBusTransportSelected = delivery.deliveryTransportType === 'eco-bus' || delivery?.selectedOption?.optionId === 'eco-bus';

  const showBusTransportSection = busTransportData?.price && isBusTransportSelected;

  const isBusScheduleInvalid = showBusTransportSection && isBusLegInPast;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { maxHeight: '90vh' } }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Package size={24} />
          <Box>
            <Typography variant="h6" fontWeight={700}>
              {isEditMode
                ? t('delivery.deliveryDetails') || 'Delivery Details'
                : t('delivery.reviewDelivery') || 'Review Your Delivery'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {isEditMode
                ? t('delivery.editDetailsDescription') || 'Edit pickup, delivery and billing details'
                : t('delivery.reviewDescription') || 'Review and edit details before confirming'}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={3}>
          <Box>
            <PickupSection
      
              address={origin}

              pickupTime={pickupTime}
              latestPickUpTime={latestPickUpTime}
              hasTimeWindow={hasTimeWindow}

              setPickupTime={setter('pickupTime')} //  nouto aikaisintaan
              setHasTimeWindow={setter('hasTimeWindow')} // aikaväli päälle/pois
              setLatestPickUpTime={setter('latestPickUpTime')} // nouto viimeistään


              apartmentNumber={pickupApartment}
              setApartmentNumber={setter('pickupApartment')}
              floor={pickupFloor}
              setFloor={setter('pickupFloor')}
              buildingName={pickupBuilding}
              setBuildingName={setter('pickupBuilding')}
              accessCode={pickupAccessCode}
              setAccessCode={setter('pickupAccessCode')}
              instructions={pickupInstructions}
              setInstructions={setter('pickupInstructions')}
              name={senderName}
              setName={setter('senderName')}
              phone={senderPhone}
              setPhone={setter('senderPhone')}
              email={senderEmail}
              setEmail={setter('senderEmail')}
              inModal={true}
            />
          </Box>

          <Divider />

          {showBusTransportSection && (
            <>
              <Box>
                <BusTransportSection
                  busTransportData={busTransportData}
                  onEditBusOption={onEditBusOption}
                  inModal={true}
                />
                
                {/* ✅ Show warning if bus leg is in the past */}
                {isBusLegInPast && (
                  <Box
                    sx={{
                      mt: 2,
                      p: 2,
                      bgcolor: 'warning.50',
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'warning.main',
                    }}
                  >
                    <Typography variant="body2" color="warning.dark">
                      {t('delivery.updateBusSchedule') || 'Päivitä bussivuoro'}
                    </Typography>
                  </Box>
                )}
              </Box>
              <Divider />
            </>
          )}

          <Box>
            <DeliverySection

              address={destination}

              targetTime={targetTime}
              hasTimeWindow={hasTimeWindow}
              earliestTargetTime={earliestTargetTime}


              setTargetTime={setter('targetTime')} // toimitus viimeistään
              setHasTimeWindow={setter('hasTimeWindow')} // aikaväli päälle/pois
              setEarliestTargetTime={setter('earliestTargetTime')} // toimitus aikaisintaan



              apartmentNumber={deliveryApartment}
              setApartmentNumber={setter('deliveryApartment')}
              floor={deliveryFloor}
              setFloor={setter('deliveryFloor')}
              buildingName={deliveryBuilding}
              setBuildingName={setter('deliveryBuilding')}
              accessCode={deliveryAccessCode}
              setAccessCode={setter('deliveryAccessCode')}
              instructions={deliveryInstructions}
              setInstructions={setter('deliveryInstructions')}
              name={recipientName}
              setName={setter('recipientName')}
              phone={recipientPhone}
              setPhone={setter('recipientPhone')}
              email={recipientEmail}
              setEmail={setter('recipientEmail')}
              inModal={true}


            />
          </Box>

          <Divider />

          <Box>
            <BillingSection
              paymentMethod={paymentMethod}
              setPaymentMethod={setter('paymentMethod')}
              yNumber={yNumber}
              setYNumber={setter('yNumber')}
              companyName={companyName}
              setCompanyName={setter('companyName')}
              paymentReference={paymentReference}
              setPaymentReference={setter('paymentReference')}
              billingDepartment={billingDepartment}
              setBillingDepartment={setter('billingDepartment')}
              inModal={true}
            />
          </Box>

          <Divider />

          <Box>
            <SpecialOptionsSection
              returnParcels={returnParcels}
              setReturnParcels={setter('returnParcels')}
              returnMethod={returnMethod}
              setReturnMethod={setter('returnMethod')}
              temperatureControl={temperatureControl}
              setTemperatureControl={setter('temperatureControl')}
              canBeLeftAtDoor={canBeLeftAtDoor}
              setCanBeLeftAtDoor={setter('canBeLeftAtDoor')}
              inModal={true}
            />
          </Box>

          {/* ✅ Price summary only shown in confirm mode */}
          {!isEditMode && (
            <>
              <Divider />
              <Box
                sx={{
                  p: 2,
                  bgcolor: 'primary.50',
                  borderRadius: 1,
                  border: '2px solid',
                  borderColor: 'primary.main',
                }}
              >
                <Stack spacing={1}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      {t(`${selectedTransportLabel}`) || t('delivery.delivery')}
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {estimatedPrice ? `€${estimatedPrice}` : '-'}
                    </Typography>
                  </Box>

                  {hasTimeWindow && timeWindowFee > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        {t('delivery.timeWindow') || 'Time Window'}
                      </Typography>
                      <Typography variant="body2" fontWeight={500}>
                        €{timeWindowFee.toFixed(2)}
                      </Typography>
                    </Box>
                  )}

                  <Divider />

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="subtitle1" fontWeight={700}>
                      {t('delivery.total') || 'Total'}
                    </Typography>
                    <Typography variant="h6" fontWeight={700} color="primary">
                      €{totalPrice || '0.00'}
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            </>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} size="large">
          {isEditMode ? t('common.done') || 'Done' : t('common.cancel') || 'Cancel'}
        </Button>
        {/* ✅ Confirm button only shown in confirm mode */}
        {!isEditMode && (
  <Button
    onClick={onConfirm}
    variant="contained"
    size="large"
    sx={{ minWidth: 120 }}
    disabled={isBusScheduleInvalid}  // ✅ Disable if bus schedule is invalid
  >
    {t('delivery.confirmAndPay') || 'Confirm & Pay'}
  </Button>
)}
      </DialogActions>
    </Dialog>
  );
}